// features/workstations/index.js — WORKSTATIONS FEATURE ROUTER
// ═══════════════════════════════════════════════════════════
// Two screens:
//   1. WorkstationHome — command-center cards landing page
//   2. WorkstationShell — 9-tab interior for a selected workstation
//
// State that flips between them lives here. When the user
// selects a workstation by slug, we also pre-compute the
// "sibling workstations" under the same domain so the shell's
// left-panel switcher tabs render correctly (per Doc 4).
//
// On entering the shell, we collapse the AppSidebar (same
// pattern Solo Workspace and Domain Briefing already use).
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import WorkstationHome from './home';
import WorkstationShell from './shell';

export default function WorkstationsFeature({
  userId,
  userRecord,
  supabase,
  onSidebarCollapse,
}) {
  // null = on Home. string = entered Workstation by slug.
  // Commit 70 — persist across refreshes via localStorage.
  const [activeSlug, setActiveSlug] = useState(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem('shift_active_workstation_slug') || null;
  });
  const [siblingWorkstations, setSiblingWorkstations] = useState([]);

  // Helper that wraps setActiveSlug + persists to localStorage
  function persistAndSetSlug(slug) {
    setActiveSlug(slug);
    if (typeof window !== 'undefined') {
      try {
        if (slug) window.localStorage.setItem('shift_active_workstation_slug', slug);
        else window.localStorage.removeItem('shift_active_workstation_slug');
      } catch { /* ignore */ }
    }
  }

  // When entering a workstation, collapse the AppSidebar so the
  // 148px panel + main has more room. Same pattern Solo uses.
  useEffect(() => {
    if (activeSlug) onSidebarCollapse?.(true);
  }, [activeSlug, onSidebarCollapse]);

  // Commit 70 — on mount with a restored activeSlug, fetch siblings
  // so the topbar switcher renders correctly without re-clicking.
  useEffect(() => {
    if (!activeSlug || siblingWorkstations.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
        const res = await fetch(`/api/workstations/list${qs}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const all = data.workstations || [];
        // Find this workstation's domain via the slug match
        const me = all.find((w) => w.slug === activeSlug);
        if (!me) return;
        const siblings = all.filter((w) => w.domain_key === me.domain_key);
        setSiblingWorkstations(siblings);
      } catch (err) {
        console.warn('[workstations] sibling restore-load failed:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [activeSlug, userId, siblingWorkstations.length]);

  // When selecting a workstation, also fetch its siblings (same
  // domain) so the shell's switcher tabs work without an extra
  // round-trip.
  async function handleOpenWorkstation(workstation) {
    persistAndSetSlug(workstation.slug);
    // Pull all workstations and filter to those sharing this domain
    try {
      const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
      const res = await fetch(`/api/workstations/list${qs}`, { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const siblings = (data.workstations || []).filter(
        w => w.domain_key === workstation.domain_key
      );
      setSiblingWorkstations(siblings);
    } catch (err) {
      console.warn('[workstations] sibling load failed:', err.message);
    }
  }

  function handleSwitchWorkstation(workstation) {
    persistAndSetSlug(workstation.slug);
    // Sibling list stays the same (same domain)
  }

  function handleBackToHome() {
    persistAndSetSlug(null);
    setSiblingWorkstations([]);
  }

  function handleCreateNew() {
    // Phase 5 routes to orientation. For Phase 1+2 — minimal
    // alert with "coming soon" so the button doesn't dead-end.
    if (typeof window !== 'undefined') {
      window.alert(
        'Workstation orientation ships in Phase 5 — a 7-minute conversation that builds your config in real time. For now, your seeded Day Trading and Long-Term 10x workstations are loaded.'
      );
    }
  }

  if (!activeSlug) {
    return (
      <WorkstationHome
        userId={userId}
        userRecord={userRecord}
        onOpenWorkstation={handleOpenWorkstation}
        onCreateNew={handleCreateNew}
      />
    );
  }

  return (
    <WorkstationShell
      userId={userId}
      userRecord={userRecord}
      supabase={supabase}
      slug={activeSlug}
      siblingWorkstations={siblingWorkstations}
      onBackToHome={handleBackToHome}
      onSwitchWorkstation={handleSwitchWorkstation}
    />
  );
}
